import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService from "api/budgetService";
import { ISettleableEngagement } from "dto/Budget/IBudgetSettlement";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

const pct = (value: number, total: number) =>
  total > 0 ? Math.min(100, Math.max(0, (value / total) * 100)) : 0;

export interface SettleInvoiceFormProps {
  invoiceId: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  onClose: () => void;
  /** Ukryj własny nagłówek (gdy renderowany wewnątrz InvoicePdfSlide). */
  hideHeader?: boolean;
}

const SettleInvoiceForm = ({
  invoiceId,
  invoiceNumber,
  invoiceAmount,
  onClose,
  hideHeader = false,
}: SettleInvoiceFormProps) => {
  const [engagement, setEngagement] = useState<ISettleableEngagement | null>(null);
  const [proforma, setProforma] = useState(false);
  const [amounts, setAmounts] = useState<Record<string, string>>({});

  const { data: settleable = [] } = budgetService.useGetSettleableEngagements();
  const { data: existing = [] } = budgetService.useGetInvoiceSettlements(invoiceId);
  const { data: settlement } = budgetService.useGetEngagementSettlement(
    engagement?.Id ?? "",
    invoiceId,
    !!engagement,
  );
  const { mutate: save, isLoading } = budgetService.useSaveSettlement();
  const { mutate: del } = budgetService.useDeleteSettlement();

  useEffect(() => {
    if (!settlement) return;
    const init: Record<string, string> = {};
    settlement.Quotas.forEach((q) => {
      init[q.EngagementQuotaId] = String(q.Amount);
    });
    setAmounts(init);
    setProforma(settlement.ProForma);
  }, [settlement]);

  const quotas = useMemo(() => settlement?.Quotas ?? [], [settlement]);

  const total = useMemo(
    () =>
      proforma
        ? 0
        : quotas.reduce(
            (s, q) => s + (parseFloat(amounts[q.EngagementQuotaId]) || 0),
            0,
          ),
    [quotas, amounts, proforma],
  );

  const settledOnInvoice = existing.reduce((s, x) => s + x.Amount, 0);

  const cap = (engagedAmount: number, settledByOthers: number) =>
    Math.max(engagedAmount - settledByOthers, 0);

  const fillFull = () =>
    setAmounts(
      Object.fromEntries(
        quotas.map((q) => [
          q.EngagementQuotaId,
          String(cap(q.EngagedAmount, q.SettledByOthers)),
        ]),
      ),
    );

  const clearAll = () =>
    setAmounts(
      Object.fromEntries(quotas.map((q) => [q.EngagementQuotaId, "0"])),
    );

  const exceeds = quotas.some((q) => {
    const v = parseFloat(amounts[q.EngagementQuotaId]) || 0;
    return !proforma && v > cap(q.EngagedAmount, q.SettledByOthers);
  });

  const canSave =
    !!engagement &&
    !exceeds &&
    (proforma || quotas.some((q) => (parseFloat(amounts[q.EngagementQuotaId]) || 0) > 0));

  const handleSave = () => {
    if (!canSave || !settlement) return;
    save(
      {
        LinkId: settlement.LinkId ?? null,
        EngagementId: settlement.EngagementId,
        KSeFInvoiceId: invoiceId,
        ProForma: proforma,
        Quotas: quotas.map((q) => ({
          EngagementQuotaId: q.EngagementQuotaId,
          Amount: proforma ? 0 : parseFloat(amounts[q.EngagementQuotaId]) || 0,
        })),
      },
      {
        onSuccess: () => {
          notification({
            title: "Zapisano rozliczenie",
            text: "Rozliczenie zaangażowania zostało zapisane.",
            type: ENotificationType.INFO,
          });
        },
        onError: (err) => {
          const message =
            (err as { response?: { data?: { Message?: string } } })?.response
              ?.data?.Message ?? "Nie udało się zapisać rozliczenia.";
          notification({
            title: "Nie udało się zapisać",
            text: message,
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      {!hideHeader && (
        <div className={styles.header}>
          <span className={styles.title}>
            Rozliczenie faktury{invoiceNumber ? ` ${invoiceNumber}` : ""}
          </span>
          <IconButton onClick={onClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.summary}>
          {invoiceAmount != null && (
            <span>Kwota faktury: {formatMoney(invoiceAmount)}</span>
          )}
          <span>Rozliczono na fakturze: {formatMoney(settledOnInvoice)}</span>
        </div>

        {existing.length > 0 && (
          <div className={styles.existing}>
            {existing.map((x) => (
              <div className={styles.existingRow} key={x.LinkId}>
                <span className={styles.existingSig}>{x.Signature}</span>
                <span className={styles.existingTitle}>{x.Title}</span>
                <span className={styles.existingAmount}>
                  {x.ProForma ? "zerowe" : formatMoney(x.Amount)}
                </span>
                <IconButton size="small" title="Usuń rozliczenie" onClick={() => del(x.LinkId)}>
                  ×
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <Autocomplete
          size="small"
          options={settleable}
          value={engagement}
          onChange={(_, val) => setEngagement(val)}
          getOptionLabel={(e) =>
            `${e.Signature} — ${e.Title ?? e.ExpenseTitle} (${formatMoney(e.EngagedAmount)})`
          }
          isOptionEqualToValue={(o, v) => o.Id === v.Id}
          openOnFocus
          noOptionsText="Brak zaangażowań do rozliczenia"
          renderInput={(params) => (
            <TextField {...params} size="small" label="Zaangażowanie do rozliczenia" />
          )}
        />

        {engagement && settlement && (
          <>
            <div className={styles.toolbar}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={proforma}
                    onChange={(e) => setProforma(e.target.checked)}
                  />
                }
                label="Rozliczenie zerowe (pro forma)"
              />
              {!proforma && (
                <div className={styles.toolbarButtons}>
                  <button className={styles.linkButton} onClick={fillFull}>
                    Rozlicz w całości
                  </button>
                  <button className={styles.linkButton} onClick={clearAll}>
                    Wyzeruj
                  </button>
                </div>
              )}
            </div>

            <div className={styles.quotaHead}>
              <span>Paragraf</span>
              <span>Rozliczenie</span>
              <span className={styles.right}>Pozostało</span>
              <span className={styles.right}>Kwota</span>
            </div>

            {quotas.map((q) => {
              const amount = proforma
                ? 0
                : parseFloat(amounts[q.EngagementQuotaId]) || 0;
              const remaining = cap(q.EngagedAmount, q.SettledByOthers);
              const othersPct = pct(q.SettledByOthers, q.EngagedAmount);
              const currentPct = Math.min(100 - othersPct, pct(amount, q.EngagedAmount));
              const percent = Math.round(pct(q.SettledByOthers + amount, q.EngagedAmount));
              const over = !proforma && amount > remaining;
              return (
                <div className={styles.quotaRow} key={q.EngagementQuotaId}>
                  <span className={styles.paragraph}>{q.ParagraphSymbol}</span>
                  <div className={styles.progress}>
                    <div className={styles.bar}>
                      <div className={styles.barOthers} style={{ width: `${othersPct}%` }} />
                      <div className={styles.barCurrent} style={{ width: `${currentPct}%` }} />
                    </div>
                    <span className={styles.percent}>{percent}%</span>
                  </div>
                  <span className={styles.right}>{formatMoney(remaining)}</span>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    disabled={proforma}
                    error={over}
                    value={proforma ? "0" : amounts[q.EngagementQuotaId] ?? ""}
                    onChange={(e) =>
                      setAmounts((prev) => ({
                        ...prev,
                        [q.EngagementQuotaId]: e.target.value,
                      }))
                    }
                  />
                </div>
              );
            })}

            <div className={styles.total}>
              <span>Rozliczana kwota</span>
              <strong>{formatMoney(total)}</strong>
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.submit}
          disabled={!canSave || isLoading}
          onClick={handleSave}
        >
          Zapisz rozliczenie
        </button>
      </div>
    </div>
  );
};

export default SettleInvoiceForm;
